import {
  type ApiError,
  type CreateThreadRequest,
  type MessageResponse,
  MessageResponseSchema,
  type PaginatedMessagesResponse,
  PaginatedMessagesResponseSchema,
  type RateLimitInfo,
  type StreamEvent,
  StreamEventSchema,
  type ThreadResponse,
  ThreadResponseSchema,
  type ThreadWithMessagesResponse,
  ThreadWithMessagesResponseSchema,
  type UiConfig,
  UiConfigSchema,
} from "@/types"

/**
 * Callback type for streaming events
 */
export type StreamEventCallback = (event: StreamEvent) => void;

/**
 * Result from a streaming message send
 */
export interface StreamMessageResult {
  rateLimitInfo: RateLimitInfo | null;
}

/**
 * API client for chat backend communication with SSE streaming support
 */
export class ApiClient {
  private baseUrl: string
  private getAuthToken: () => string | null

  private decoder = new TextDecoder("utf-8")

  constructor(baseUrl: string, getAuthToken: () => string | null) {
    this.baseUrl = baseUrl
    this.getAuthToken = getAuthToken
  }

  /**
   * Get authorization headers
   */
  private getHeaders(): HeadersInit {
    const token = this.getAuthToken()
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    }
  }

  /**
   * Parse rate limit info from response headers
   */
  private parseRateLimitInfo(response: Response): RateLimitInfo | null {
    const limit = response.headers.get("X-RateLimit-Limit")
    const remaining = response.headers.get("X-RateLimit-Remaining")
    const reset = response.headers.get("X-RateLimit-Reset")

    if (limit && remaining && reset) {
      return {
        limit: parseInt(limit, 10),
        remaining: parseInt(remaining, 10),
        reset: parseInt(reset, 10),
      }
    }
    return null
  }

  /**
   * Handle error responses
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    const error: ApiError = await response.json().catch(() => ({
      code: response.status === 429 ? "RATE_LIMITED" : "UNKNOWN_ERROR",
      message:
        response.status === 429
          ? "Rate limit exceeded. Please wait before sending another message."
          : `Request failed with status ${response.status}`,
    }))
    throw error
  }

  /**
   * Make an authenticated fetch request
   */
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    })

    if (!response.ok) {
      await this.handleErrorResponse(response)
    }

    return response.json()
  }

  /**
   * Fetch runtime UI feature flags. Public endpoint; fails closed (defaults
   * applied by the schema) so a missing/partial response hides features.
   */
  async getUiConfig(signal?: AbortSignal): Promise<UiConfig> {
    const response = await this.fetch<unknown>("/config/ui", { signal })
    return UiConfigSchema.parse(response)
  }

  /**
   * Create a new conversation thread
   */
  async createThread(
    request: CreateThreadRequest = {},
  ): Promise<ThreadResponse> {
    const response = await this.fetch<unknown>("/threads", {
      method: "POST",
      body: JSON.stringify(request),
    })

    const result = ThreadResponseSchema.safeParse(response)
    if (!result.success) {
      throw {
        code: "INVALID_RESPONSE",
        message: "Received invalid thread response format from server",
      } as ApiError
    }

    return result.data
  }

  /**
   * Get a thread with all its messages
   */
  async getThread(threadId: string): Promise<ThreadWithMessagesResponse> {
    const response = await this.fetch<unknown>(`/threads/${threadId}`)

    const result = ThreadWithMessagesResponseSchema.safeParse(response)
    if (!result.success) {
      throw {
        code: "INVALID_RESPONSE",
        message: "Received invalid thread response format from server",
      } as ApiError
    }

    return result.data
  }

  /**
   * Get paginated messages for a thread
   */
  async getMessages(
    threadId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<PaginatedMessagesResponse> {
    const params = new URLSearchParams()
    if (options.limit) params.set("limit", options.limit.toString())
    if (options.offset) params.set("offset", options.offset.toString())

    const queryString = params.toString()
    const endpoint = `/threads/${threadId}/messages${queryString ? `?${queryString}` : ""}`

    const response = await this.fetch<unknown>(endpoint)

    const result = PaginatedMessagesResponseSchema.safeParse(response)
    if (!result.success) {
      throw {
        code: "INVALID_RESPONSE",
        message: "Received invalid messages response format from server",
      } as ApiError
    }

    return result.data
  }

  /**
   * Send a message and stream the response via SSE
   * Returns an async generator that yields stream events
   */
  async *sendMessageStream(
    threadId: string,
    content: string,
    signal?: AbortSignal,
  ): AsyncGenerator<StreamEvent, StreamMessageResult, unknown> {
    const response = await fetch(
      `${this.baseUrl}/threads/${threadId}/messages`,
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ content }),
        signal,
      },
    )

    if (!response.ok) {
      await this.handleErrorResponse(response)
    }

    const rateLimitInfo = this.parseRateLimitInfo(response)

    if (!response.body) {
      throw {
        code: "NO_RESPONSE_BODY",
        message: "Server did not return a response body",
      } as ApiError
    }

    const reader = response.body.getReader()

    let buffer = ""

    try {
      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        buffer += this.decoder.decode(value, { stream: true })

        // Process complete lines (SSE events are newline-delimited JSON)
        const lines = buffer.split("\n")
        buffer = lines.pop() || "" // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) continue

          try {
            const parsed = JSON.parse(trimmed)
            const result = StreamEventSchema.safeParse(parsed)

            if (result.success) {
              yield result.data
            } else {
              console.warn("Invalid stream event:", result.error)
            }
          } catch (e) {
            // Skip malformed JSON lines
            console.warn("Failed to parse stream event:", trimmed, e)
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer.trim())
          const result = StreamEventSchema.safeParse(parsed)
          if (result.success) {
            yield result.data
          }
        } catch (e) {
          // Ignore final incomplete data
          console.warn("Failed to parse final stream event:", buffer.trim(), e)
        }
      }
    } finally {
      reader.releaseLock()
    }

    return { rateLimitInfo }
  }

  /**
   * Vote on a message's helpfulness
   */
  async voteOnMessage(
    threadId: string,
    messageId: string,
    wasHelpful: boolean | null,
  ): Promise<MessageResponse> {
    const response = await this.fetch<unknown>(
      `/threads/${threadId}/messages/${messageId}/vote`,
      {
        method: "PATCH",
        body: JSON.stringify({ was_helpful: wasHelpful }),
      },
    )

    const result = MessageResponseSchema.safeParse(response)
    if (!result.success) {
      throw {
        code: "INVALID_RESPONSE",
        message: "Received invalid message response format from server",
      } as ApiError
    }

    return result.data
  }
}

// Create a factory function for the API client
let apiClientInstance: ApiClient | null = null

export function getApiClient(getAuthToken: () => string | null): ApiClient {
  if (!apiClientInstance) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL

    if (!baseUrl) {
      throw new Error("VITE_API_BASE_URL environment variable is not set")
    }

    // replace trailing slash
    const normalizedBaseUrl = baseUrl.replace(/\/$/, "")

    apiClientInstance = new ApiClient(normalizedBaseUrl, getAuthToken)
  }
  return apiClientInstance
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetApiClient(): void {
  apiClientInstance = null
}
